import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import Box from "@mui/material/Box";  
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import style from "../../tecnicalCard/TechCardStyles";
import { AhuType } from "../../../models/unitType";
import { brandTranslation } from "../../../utils/brandUtils";
import { sizeOfUnitMapping } from "../../../utils/unitConstants";
import { getInstallationType } from "../../../utils/helper";

const Calculation = () => {
  const { watch } = useFormContext();
  const { t } = useTranslation();
  const theme = useTheme();
  const params = useParams();
  const baseUnitType = params?.unitType ? parseInt(params.unitType, 10) : null;

  // Watch all form values
  const { isClivet } = useSelector((state) => state.brand);
  const airFlowCalc = watch("airFlowCalc");
  const area = watch("area");
  const height = watch("height");
  const coils = watch("coils");
  const buildingType = watch("buildingType");
  const changeRate = watch("changeRate");
  const dropSupply = watch("dropSupply");
  const flowSupply = watch("flowSupply");
  const flowReturn = watch("flowReturn");
  const dropReturn = watch("dropReturn");
  const ecoDesign = watch("ecoDesign");
  const casingQuality = watch("casingQuality");
  const installation = watch("installation");
  const Type = watch("Type");
  const isLinePrime =
    baseUnitType === AhuType.LINE_PRIME || Type === AhuType.LINE_PRIME;
  const heatRecovery = watch("heatRecovery");
  const heatRecoveryPlateType = watch("heatRecoveryPlateType");
  const sizeOfUnit = watch("sizeOfUnit");
  const unitDivision = watch("unitDivision");
  const summerOutdoorTemperature = watch("summerOutdoorTemperature");
  const summerWetBulbTemperature = watch("summerWetBulbTemperature");
  const summerDewPointTemperature = watch("summerDewPointTemperature");
  const summerOutdoorHumidity = watch("summerOutdoorHumidity");
  const summerIndoorTemperature = watch("summerIndoorTemperature");
  const summerIndoorHumidity = watch("summerIndoorHumidity");
  const coolerType = watch("coolerType");
  const coolerConcentration = watch("coolerConcentration");
  const coolerTemperatureIn = watch("coolerTemperatureIn");
  const winterOutdoorTemperature = watch("winterOutdoorTemperature");
  const winterOutdoorHumidity = watch("winterOutdoorHumidity");
  const heaterType = watch("heaterType");
  const heaterConcentration = watch("heaterConcentration");
  const heaterTemperatureIn = watch("heaterTemperatureIn");
  const elevation = watch("elevation");
  const winterIndoorTemperature = watch("winterIndoorTemperature");
  const winterIndoorHumidity = watch("winterIndoorHumidity");
  const fineFilterType = watch("fineFilterType");
  const supplyAirFilterClass = watch("supplyAirFilterClass");
  const returnAirFilterClass = watch("returnAirFilterClass");
  const fanMode = watch("fanMode");
  const coolerAirInputParam = watch("coolerAirInputParam");
  const coolerAirInputParamValue = watch("coolerAirInputParamValue");
  const coolerWaterInputParam = watch("coolerWaterInputParam");
  const coolerWaterInputParamValue = watch("coolerWaterInputParamValue");
  const heaterAirInputParam = watch("heaterAirInputParam");
  const heaterAirInputParamValue = watch("heaterAirInputParamValue");
  const heaterWaterInputParam = watch("heaterWaterInputParam");
  const heaterWaterInputParamValue = watch("heaterWaterInputParamValue");

  // Map Type enum to display string
const unitTypeDisplay = useMemo(() => {
  switch (Type) {
    case AhuType.COMPACT:
      return isClivet ? t("clivet.framelessSelect.productCards.compactAhu.title") : t("unitType.compactPrimeHorizontal");

    case AhuType.COMPACT_VERTICAL:
      return isClivet ? t("clivet.framelessSelect.productCards.compactAhu.title") : t("unitType.compactPrimeVertical");

    case AhuType.ENERGY_PRIME_STANDARD:
      return isClivet ? t("clivet.framelessSelect.productCards.energyAhu.title") : t("unitType.energyPrimeStandard");

    case AhuType.ENERGY_PRIME_GENIUS:
      return isClivet ? t("clivet.framelessSelect.productCards.energyAhu.title") : t("unitType.energyPrimeGenius");

    case AhuType.AQUA_PRIME_STANDARD:
      return isClivet ? "Not in use" : t("unitType.aquaPrimeStandard");

    case AhuType.AQUA_PRIME_GENIUS:
      return isClivet ? "Not in use" : t("unitType.aquaPrimeGenius");

    default:
      return "";
  }
}, [Type, isClivet]);

  const filterClassFullName = {
    M6: "ePM10 70% (M6)",
    F7: "ePM1 60% (F7)",
    F8: "ePM1 70% (F8)",
    F9: "ePM1 80% (F9)",
  };

  return (
    <Container component="main" sx={{ mb: 4 }}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, md: 5 },
          align: "center",
        }}
      >
        <Box
          sx={{
            ...style["section-wrapper calc"],
            borderColor: theme.palette.secondary.main,
          }}
        >
          <Box sx={{ ...style.flex }}>
            {/* LEFT */}
            <Box
              sx={{
                ...style.left,
                paddingRight: "5px",
                borderRight: {
                  lg: `2px solid ${theme.palette.secondary.main}`,
                },
                width: { xs: "100%", lg: "50%" },
              }}
            >
              <Typography
                sx={{
                  textAlign: "center",
                  fontSize: { xs: "16px", lg: "18px" },
                  fontWeight: "600",
                }}
              >
                {t("mainform.info")}
              </Typography>
              {!airFlowCalc && (
                <>
                  <Box sx={{ ...style["md-flex"] }}>
                    <Box sx={{ ...style["label calc"] }}>
                      {t("calculation.area")}:
                    </Box>
                    <Box sx={{ ...style["value calc"] }}>
                      <b>
                        {area} m<sup>2</sup>
                      </b>
                    </Box>
                  </Box>

                  {!airFlowCalc && (
                    <Box
                      sx={{
                        ...style["md-flex"],
                        display: { xs: "flex", lg: "none" },
                      }}
                    >
                      <Box sx={{ ...style["label calc"] }}>
                        {t("calculation.height")}:
                      </Box>
                      <Box sx={{ ...style["value calc"] }}>
                        <b>{height} m</b>
                      </Box>
                    </Box>
                  )}

                  <Box sx={{ ...style["md-flex"] }}>
                    <Box sx={{ ...style["label calc"] }}>
                      {t("calculation.buildingType")}:
                    </Box>
                    <Box sx={{ ...style["value calc"] }}>
                      <b>{t(`buildingTypes.${buildingType}`)}</b>
                    </Box>
                  </Box>
                </>
              )}
              {!airFlowCalc && (
                <Box
                  sx={{
                    ...style["md-flex"],
                    display: { xs: "flex", lg: "none" },
                  }}
                >
                  <Box sx={{ ...style["label calc"] }}>
                    {t("calculation.changeRate")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b>{changeRate}</b>
                  </Box>
                </Box>
              )}
              <Box sx={{ ...style["md-flex"] }}>
                <Box sx={{ ...style["label calc"] }}>
                  {t("calculation.dropSupply")}:
                </Box>
                <Box sx={{ ...style["value calc"] }}>
                  <b>{dropSupply} Pa</b>
                </Box>
              </Box>
              {!isLinePrime && (
                <Box
                  sx={{
                    ...style["md-flex"],
                    display: { xs: "flex", lg: "none" },
                  }}
                >
                  <Box sx={{ ...style["label calc"] }}>
                    {t("calculation.dropReturn")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b>{dropReturn} Pa</b>
                  </Box>
                </Box>
              )}
              <Box sx={{ ...style["md-flex"] }}>
                <Box sx={{ ...style["label calc"] }}>
                  {t("calculation.flowSupply")}:
                </Box>
                <Box sx={{ ...style["value calc"] }}>
                  <b>
                    {flowSupply} m<sup>3</sup>/h
                  </b>
                </Box>
              </Box>
              {!isLinePrime && (
                <Box
                  sx={{
                    ...style["md-flex"],
                    display: { xs: "flex", lg: "none" },
                  }}
                >
                  <Box sx={{ ...style["label calc"] }}>
                    {t("calculation.flowReturn")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b>
                      {flowReturn} m<sup>3</sup>/h
                    </b>
                  </Box>
                </Box>
              )}

              {/* REQUIREMENTS */}
              <Typography
                sx={{
                  textAlign: "center",
                  fontSize: { xs: "16px", lg: "18px" },
                  fontWeight: "600",
                }}
              >
                {t("mainform.requirements")}
              </Typography>
              <Box sx={{ ...style["md-flex"] }}>
                <Box sx={{ ...style["label calc"] }}>
                  {t("calculation.ecodesign")}:
                </Box>
                <Box sx={{ ...style["value calc"] }}>
                  <b> {t(`requirementsList.${ecoDesign}`)}</b>
                </Box>
              </Box>
              <Box sx={{ ...style["md-flex"] }}>
                <Box sx={{ ...style["label calc"] }}>
                  {t("calculation.casingQuality")}:
                </Box>
                <Box sx={{ ...style["value calc"] }}>
                  <b>{casingQuality}</b>
                </Box>
              </Box>
              <Box sx={{ ...style["md-flex"] }}>
                <Box sx={{ ...style["label calc"], color: "white" }}>-</Box>
                <Box sx={{ ...style["value calc"], color: "white" }}>-</Box>
              </Box>
              <Box
                sx={{
                  ...style["md-flex"],
                  display: { xs: "none", lg: "flex" },
                }}
              >
                <Box
                  sx={{
                    ...style["label calc"],
                    color: theme.palette.primary.light,
                  }}
                >
                  -
                </Box>
                <Box
                  sx={{
                    ...style["value calc"],
                    color: theme.palette.primary.light,
                  }}
                >
                  -
                </Box>
              </Box>
              <Box
                sx={{
                  ...style["md-flex"],
                  display: { xs: "none", lg: "flex" },
                }}
              >
                <Box
                  sx={{
                    ...style["label calc"],
                    color: theme.palette.primary.light,
                  }}
                >
                  -
                </Box>
                <Box
                  sx={{
                    ...style["value calc"],
                    color: theme.palette.primary.light,
                  }}
                >
                  -
                </Box>
              </Box>

              {/* TYPE OF UNIT - MOBILE */}
              <Box sx={{ display: { xs: "block", lg: "none" } }}>
                <Typography
                  sx={{
                    textAlign: "center",
                    fontSize: { xs: "16px", lg: "18px" },
                    fontWeight: "600",
                  }}
                >
                  {t("mainform.typeOfUnit")}
                </Typography>
                <Box sx={{ ...style["md-flex"] }}>
                  <Box sx={{ ...style["label calc"] }}>
                    {t("calculation.installation")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b>{t(`unitTypeList.${installation}`)}</b>
                  </Box>
                </Box>
                <Box sx={{ ...style["md-flex"] }}>
                  <Box sx={{ ...style["label calc"] }}>
                    {t("calculation.unitType")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b>{unitTypeDisplay}</b>
                  </Box>
                </Box>
                <Box sx={{ ...style["md-flex"] }}>
                  <Box sx={{ ...style["label calc"] }}>
                    {t("unitType.coils")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b>{t("unitType." + coils)}</b>
                  </Box>
                </Box>
                <Box sx={{ ...style["md-flex"] }}>
                  <Box sx={{ ...style["label calc"] }}>
                    {t("calculation.sizeOfUnit")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b>{sizeOfUnitMapping[sizeOfUnit] || sizeOfUnit}</b>
                  </Box>
                </Box>

                <Box sx={{ ...style["md-flex"] }}>
                  <Box sx={{ ...style["label calc"] }}>
                    {t("calculation.heatRecovery")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b> {t(`unitTypeList.${heatRecovery}`)}</b>
                  </Box>
                </Box>
                {heatRecovery === "Plate heat exchanger" && (
                  <Box sx={{ ...style["md-flex"] }}>
                    <Box sx={{ ...style["label calc"] }}>
                      {t("calculation.heatRecoveryPlateType")}:
                    </Box>
                    <Box sx={{ ...style["value calc"] }}>
                      <b> {t(`unitType.${heatRecoveryPlateType}`)}</b>
                    </Box>
                  </Box>
                )}
                <Box sx={{ ...style["md-flex"] }}>
                  <Box sx={{ ...style["label calc"] }}>
                    {t("calculation.unitDivision")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b> {t(`unitTypeList.${unitDivision}`)}</b>
                  </Box>
                </Box>
              </Box>

              {/* OPERATING POINT - SUMMER MODE */}
              <Typography
                sx={{
                  textAlign: "center",
                  fontSize: { xs: "16px", lg: "18px" },
                  fontWeight: "600",
                }}
              >
                {t("mainform.operatingpoint")} - {t("mainform.summerMode")}
              </Typography>
              <Box sx={{ ...style["md-flex"] }}>
                <Box sx={{ ...style["label calc"] }}>
                  {t("calculation.dryBulbTemperatureCelsius")}:
                </Box>
                <Box sx={{ ...style["value calc"] }}>
                  <b>{summerOutdoorTemperature} °C</b>
                </Box>
              </Box>
              <Box sx={{ ...style["md-flex"] }}>
                <Box sx={{ ...style["label calc"] }}>
                  {t("calculation.wetBulbTemperatureCelsius")}:
                </Box>
                <Box sx={{ ...style["value calc"] }}>
                  <b>{summerWetBulbTemperature} °C</b>
                </Box>
              </Box>
              <Box sx={{ ...style["md-flex"] }}>
                <Box sx={{ ...style["label calc"] }}>
                  {t("calculation.dewPointTemperatureCelsius")}:
                </Box>
                <Box sx={{ ...style["value calc"] }}>
                  <b>{summerDewPointTemperature} °C</b>
                </Box>
              </Box>
              <Box sx={{ ...style["md-flex"] }}>
                <Box sx={{ ...style["label calc"] }}>
                  {t("calculation.relativeHumiditySummer")}:
                </Box>
                <Box sx={{ ...style["value calc"] }}>
                  <b>{summerOutdoorHumidity} %</b>
                </Box>
              </Box>
              <Box sx={{ ...style["md-flex"] }}>
                <Box sx={{ ...style["label calc"] }}>
                  {t("calculation.summerIndoorTemperature")}:
                </Box>
                <Box sx={{ ...style["value calc"] }}>
                  <b>{summerIndoorTemperature} °C</b>
                </Box>
              </Box>
              <Box sx={{ ...style["md-flex"] }}>
                <Box sx={{ ...style["label calc"] }}>
                  {t("calculation.summerIndoorHumidity")}:
                </Box>
                <Box sx={{ ...style["value calc"] }}>
                  <b>{summerIndoorHumidity} %</b>
                </Box>
              </Box>

              {/* COOLING */}
              {(coils === "cooler" || coils === "both") && (
                <>
                  <Typography
                    sx={{
                      textAlign: "center",
                      fontSize: { xs: "16px", lg: "18px" },
                      fontWeight: "600",
                    }}
                  >
                    {t("unitType.cooler")}
                  </Typography>
                  <Box sx={{ ...style["md-flex"] }}>
                    <Box sx={{ ...style["label calc"] }}>
                      {coolerAirInputParam}:
                    </Box>
                    <Box sx={{ ...style["value calc"] }}>
                      <b>
                        {coolerAirInputParamValue}{" "}
                        {coolerAirInputParam === "Supply Temperature"
                          ? "°C"
                          : "kW"}
                      </b>
                    </Box>
                  </Box>
                  <Box sx={{ ...style["md-flex"] }}>
                    <Box sx={{ ...style["label calc"] }}>
                      {t("operatingpoint.heaterType")}:
                    </Box>
                    <Box sx={{ ...style["value calc"] }}>
                      <b>{t("operatingpoint.heatingType." + coolerType)}</b>
                    </Box>
                  </Box>
                  <Box sx={{ ...style["md-flex"] }}>
                    <Box sx={{ ...style["label calc"] }}>
                      {t("operatingpoint.concentration")}:
                    </Box>
                    <Box sx={{ ...style["value calc"] }}>
                      <b>{coolerConcentration} %</b>
                    </Box>
                  </Box>
                  <Box sx={{ ...style["md-flex"] }}>
                    <Box sx={{ ...style["label calc"] }}>
                      {t("operatingpoint.temperatureIn")}:
                    </Box>
                    <Box sx={{ ...style["value calc"] }}>
                      <b>{coolerTemperatureIn} °C</b>
                    </Box>
                  </Box>
                  <Box sx={{ ...style["md-flex"] }}>
                    <Box sx={{ ...style["label calc"] }}>
                      {coolerWaterInputParam}:
                    </Box>
                    <Box sx={{ ...style["value calc"] }}>
                      <b>
                        {coolerWaterInputParamValue}{" "}
                        {coolerWaterInputParam === "Temperature out"
                          ? "°C"
                          : "m³/h"}
                      </b>
                    </Box>
                  </Box>
                </>
              )}

              {/* OPERATING POINT - WINTER MODE - MOBILE */}
              <Box sx={{ display: { xs: "block", lg: "none" } }}>
                <Typography
                  sx={{
                    textAlign: "center",
                    fontSize: { xs: "16px", lg: "18px" },
                    fontWeight: "600",
                  }}
                >
                  {t("mainform.operatingpoint")} - {t("mainform.winterMode")}
                </Typography>
                <Box sx={{ ...style["md-flex"] }}>
                  <Box sx={{ ...style["label calc"] }}>
                    {t("calculation.elevation")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b>{elevation} m</b>
                  </Box>
                </Box>
                <Box sx={{ ...style["md-flex"] }}>
                  <Box sx={{ ...style["label calc"] }}>
                    {t("calculation.heatingDB")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b>{winterOutdoorTemperature} °C</b>
                  </Box>
                </Box>

                <Box sx={{ ...style["md-flex"] }}>
                  <Box sx={{ ...style["label calc"] }}>
                    {t("calculation.relativeHumidityWinter")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b>{winterOutdoorHumidity} %</b>
                  </Box>
                </Box>
                <Box sx={{ ...style["md-flex"] }}>
                  <Box sx={{ ...style["label calc"] }}>
                    {t("calculation.winterIndoorTemperature")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b>{winterIndoorTemperature} °C</b>
                  </Box>
                </Box>
                <Box sx={{ ...style["md-flex"] }}>
                  <Box sx={{ ...style["label calc"] }}>
                    {t("calculation.winterIndoorHumidity")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b>{winterIndoorHumidity} %</b>
                  </Box>
                </Box>

                {/* HEATER - MOBILE */}
                {(coils === "heater" || coils === "both") && (
                  <>
                    <Typography
                      sx={{
                        textAlign: "center",
                        fontSize: { xs: "16px", lg: "18px" },
                        fontWeight: "600",
                      }}
                    >
                      {t("unitType.heater")}
                    </Typography>
                    <Box sx={{ ...style["md-flex"] }}>
                      <Box sx={{ ...style["label calc"] }}>
                        {heaterAirInputParam}:
                      </Box>
                      <Box sx={{ ...style["value calc"] }}>
                        <b>
                          {heaterAirInputParamValue}{" "}
                          {heaterAirInputParam == "Supply Temperature"
                            ? "°C"
                            : "kW"}
                        </b>
                      </Box>
                    </Box>
                    <Box sx={{ ...style["md-flex"] }}>
                      <Box sx={{ ...style["label calc"] }}>
                        {t("operatingpoint.heaterType")}:
                      </Box>
                      <Box sx={{ ...style["value calc"] }}>
                        <b>{t("operatingpoint.heatingType." + heaterType)} </b>
                      </Box>
                    </Box>
                    <Box sx={{ ...style["md-flex"] }}>
                      <Box sx={{ ...style["label calc"] }}>
                        {t("operatingpoint.concentration")}:
                      </Box>
                      <Box sx={{ ...style["value calc"] }}>
                        <b>{heaterConcentration} %</b>
                      </Box>
                    </Box>
                    <Box sx={{ ...style["md-flex"] }}>
                      <Box sx={{ ...style["label calc"] }}>
                        {t("operatingpoint.temperatureIn")}:
                      </Box>
                      <Box sx={{ ...style["value calc"] }}>
                        <b>{heaterTemperatureIn} °C</b>
                      </Box>
                    </Box>
                    <Box sx={{ ...style["md-flex"] }}>
                      <Box sx={{ ...style["label calc"] }}>
                        {heaterWaterInputParam}:
                      </Box>
                      <Box sx={{ ...style["value calc"] }}>
                        <b>
                          {heaterWaterInputParamValue}{" "}
                          {heaterWaterInputParam === "Temperature out"
                            ? "°C"
                            : "m³/h"}
                        </b>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>

              {/* FILTERING */}
              <Typography
                sx={{
                  textAlign: "center",
                  fontSize: { xs: "16px", lg: "18px" },
                  fontWeight: "600",
                }}
              >
                {t("mainform.filtering")}
              </Typography>
              <Box sx={{ ...style["md-flex"] }}>
                <Box sx={{ ...style["label calc"] }}>
                  {t("calculation.fineFilterType")}:
                </Box>
                <Box sx={{ ...style["value calc"] }}>
                  <b> {t(`filteringList.${fineFilterType}`)}</b>
                </Box>
              </Box>
              <Box sx={{ ...style["md-flex"] }}>
                <Box sx={{ ...style["label calc"] }}>
                  {t("filtering.supplyFilter")}
                </Box>
                <Box sx={{ ...style["value calc"] }}>
                  <b>
                    {filterClassFullName[supplyAirFilterClass] ||
                      supplyAirFilterClass}
                  </b>
                </Box>
              </Box>
              <Box sx={{ ...style["md-flex"] }}>
                <Box sx={{ ...style["label calc"] }}>
                  {t("filtering.returnFilter")}:
                </Box>
                <Box sx={{ ...style["value calc"] }}>
                  <b>
                    {filterClassFullName[returnAirFilterClass] ||
                      returnAirFilterClass}
                  </b>
                </Box>
              </Box>
            </Box>

            {/* RIGHT */}
            <Box sx={{ ...style.right, width: { xs: "100%", lg: "50%" } }}>
              {/* PROJECT INFORMATION - DESKTOP */}
              <Box sx={{ display: { xs: "none", lg: "block" } }}>
                <Typography
                  sx={{
                    textAlign: "center",
                    fontSize: { xs: "16px", lg: "18px" },
                    fontWeight: "600",
                  }}
                >
                  {t("mainform.info")}
                </Typography>
                {!airFlowCalc && (
                  <Box sx={{ ...style["md-flex"] }}>
                    <Box sx={{ ...style["label calc"] }}>
                      {t("calculation.height")}:
                    </Box>
                    <Box sx={{ ...style["value calc"] }}>
                      <b>{height} m</b>
                    </Box>
                  </Box>
                )}
                {!airFlowCalc && (
                  <Box sx={{ ...style["md-flex"] }}>
                    <Box sx={{ ...style["label calc"] }}>
                      {t("calculation.changeRate")}:
                    </Box>
                    <Box sx={{ ...style["value calc"] }}>
                      <b>{changeRate}</b>
                    </Box>
                  </Box>
                )}
                {!isLinePrime && (
                  <>
                    <Box sx={{ ...style["md-flex"] }}>
                      <Box sx={{ ...style["label calc"] }}>
                        {t("calculation.dropReturn")}:
                      </Box>
                      <Box sx={{ ...style["value calc"] }}>
                        <b>{dropReturn} Pa</b>
                      </Box>
                    </Box>
                    <Box sx={{ ...style["md-flex"] }}>
                      <Box sx={{ ...style["label calc"] }}>
                        {t("calculation.flowReturn")}:
                      </Box>
                      <Box sx={{ ...style["value calc"] }}>
                        <b>
                          {flowReturn} m<sup>3</sup>/h
                        </b>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>

              {/* TYPE OF UNIT - DESKTOP */}
              <Box sx={{ display: { xs: "none", lg: "block" } }}>
                <Typography
                  sx={{
                    textAlign: "center",
                    fontSize: { xs: "16px", lg: "18px" },
                    fontWeight: "600",
                  }}
                >
                  {t("mainform.typeOfUnit")}
                </Typography>
                <Box sx={{ ...style["md-flex"] }}>
                  <Box sx={{ ...style["label calc"] }}>
                    {t("calculation.installation")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b>{getInstallationType(installation)}</b>
                  </Box>
                </Box>
                <Box sx={{ ...style["md-flex"] }}>
                  <Box sx={{ ...style["label calc"] }}>
                    {t("calculation.unitType")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b>{unitTypeDisplay}</b>
                  </Box>
                </Box>
                <Box sx={{ ...style["md-flex"] }}>
                  <Box sx={{ ...style["label calc"] }}>
                    {t("unitType.coils")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b>{t("unitType." + coils)}</b>
                  </Box>
                </Box>
                <Box sx={{ ...style["md-flex"] }}>
                  <Box sx={{ ...style["label calc"] }}>
                    {t("calculation.sizeOfUnit")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b>{sizeOfUnitMapping[sizeOfUnit] || sizeOfUnit}</b>
                  </Box>
                </Box>

                <Box sx={{ ...style["md-flex"] }}>
                  <Box sx={{ ...style["label calc"] }}>
                    {t("calculation.heatRecovery")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b> {t(`unitTypeList.${heatRecovery}`)}</b>
                  </Box>
                </Box>
                {heatRecovery === "Plate heat exchanger" && (
                  <Box sx={{ ...style["md-flex"] }}>
                    <Box sx={{ ...style["label calc"] }}>
                      {t("calculation.heatRecoveryPlateType")}:
                    </Box>
                    <Box sx={{ ...style["value calc"] }}>
                      <b> {t(`unitType.${heatRecoveryPlateType}`)}</b>
                    </Box>
                  </Box>
                )}
                <Box sx={{ ...style["md-flex"] }}>
                  <Box sx={{ ...style["label calc"] }}>
                    {t("calculation.unitDivision")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b> {t(`unitTypeList.${unitDivision}`)}</b>
                  </Box>
                </Box>
              </Box>

              {/* OPERATING POINT - WINTER MODE - DESKTOP */}
              <Box sx={{ display: { xs: "none", lg: "block" } }}>
                <Typography
                  sx={{
                    textAlign: "center",
                    fontSize: { xs: "16px", lg: "18px" },
                    fontWeight: "600",
                  }}
                >
                  {t("mainform.operatingpoint")} - {t("mainform.winterMode")}
                </Typography>
                <Box sx={{ ...style["md-flex"] }}>
                  <Box sx={{ ...style["label calc"] }}>
                    {t("calculation.elevation")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b>{elevation} m</b>
                  </Box>
                </Box>
                <Box sx={{ ...style["md-flex"] }}>
                  <Box sx={{ ...style["label calc"] }}>
                    {t("calculation.heatingDB")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b>{winterOutdoorTemperature} °C</b>
                  </Box>
                </Box>
                <Box sx={{ ...style["md-flex"] }}>
                  <Box sx={{ ...style["label calc"] }}>
                    {t("calculation.relativeHumidityWinter")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b>{winterOutdoorHumidity} %</b>
                  </Box>
                </Box>
                <Box sx={{ ...style["md-flex"] }}>
                  <Box sx={{ ...style["label calc"] }}>
                    {t("calculation.winterIndoorTemperature")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b>{winterIndoorTemperature} °C</b>
                  </Box>
                </Box>
                <Box sx={{ ...style["md-flex"] }}>
                  <Box sx={{ ...style["label calc"] }}>
                    {t("calculation.winterIndoorHumidity")}:
                  </Box>
                  <Box sx={{ ...style["value calc"] }}>
                    <b>{winterIndoorHumidity} %</b>
                  </Box>
                </Box>

                {/* HEATER - DESKTOP */}
                {(coils === "heater" || coils === "both") && (
                  <>
                    <Typography
                      sx={{
                        textAlign: "center",
                        fontSize: { xs: "16px", lg: "18px" },
                        fontWeight: "600",
                      }}
                    >
                      {t("unitType.heater")}
                    </Typography>
                    <Box sx={{ ...style["md-flex"] }}>
                      <Box sx={{ ...style["label calc"] }}>
                        {heaterAirInputParam}:
                      </Box>
                      <Box sx={{ ...style["value calc"] }}>
                        <b>
                          {heaterAirInputParamValue}{" "}
                          {heaterAirInputParam == "Supply Temperature"
                            ? "°C"
                            : "kW"}
                        </b>
                      </Box>
                    </Box>
                    <Box sx={{ ...style["md-flex"] }}>
                      <Box sx={{ ...style["label calc"] }}>
                        {t("operatingpoint.heaterType")}:
                      </Box>
                      <Box sx={{ ...style["value calc"] }}>
                        <b>{t("operatingpoint.heatingType." + heaterType)} </b>
                      </Box>
                    </Box>
                    <Box sx={{ ...style["md-flex"] }}>
                      <Box sx={{ ...style["label calc"] }}>
                        {t("operatingpoint.concentration")}:
                      </Box>
                      <Box sx={{ ...style["value calc"] }}>
                        <b>{heaterConcentration} %</b>
                      </Box>
                    </Box>
                    <Box sx={{ ...style["md-flex"] }}>
                      <Box sx={{ ...style["label calc"] }}>
                        {t("operatingpoint.temperatureIn")}:
                      </Box>
                      <Box sx={{ ...style["value calc"] }}>
                        <b>{heaterTemperatureIn} °C</b>
                      </Box>
                    </Box>
                    <Box sx={{ ...style["md-flex"] }}>
                      <Box sx={{ ...style["label calc"] }}>
                        {heaterWaterInputParam}:
                      </Box>
                      <Box sx={{ ...style["value calc"] }}>
                        <b>
                          {heaterWaterInputParamValue}{" "}
                          {heaterWaterInputParam === "Temperature out"
                            ? "°C"
                            : "m³/h"}
                        </b>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
              <Box sx={{ ...style["md-flex"] }}>
                <Box
                  sx={{
                    ...style["label calc"],
                    color: theme.palette.primary.light,
                  }}
                >
                  -
                </Box>
                <Box
                  sx={{
                    ...style["value calc"],
                    color: theme.palette.primary.light,
                  }}
                >
                  -
                </Box>
              </Box>

              {/* FAN */}
              <Typography
                sx={{
                  textAlign: "center",
                  fontSize: { xs: "16px", lg: "18px" },
                  fontWeight: "600",
                }}
              >
                {t("mainform.fan")}
              </Typography>
              <Box sx={{ ...style["md-flex"] }}>
                <Box sx={{ ...style["label calc"] }}>
                  {t("calculation.fanType")}:
                </Box>
                <Box sx={{ ...style["value calc"] }}>
                  <b> {t(`fanType.${fanMode}`)}</b>
                </Box>
              </Box>
              <Box sx={{ ...style["md-flex"] }}>
                <Box sx={{ ...style["label calc"], color: "white" }}>-</Box>
                <Box sx={{ ...style["value calc"], color: "white" }}>-</Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Calculation;
